/**
 * An object for testing if an IP is in the range specified by a
 * Classless Inter-Domain Routing (CIDR) specification.
 * See:  http://tools.ietf.org/html/rfc4632#section-3.1
 * Example: 92.128.0.0/17 specifies a range of addresses each of
 * which must have the same leading 17 bits as those in 92.128.0.0.
 *
 * Ported from [optly.cidr@910eca63e4f3eed8604542f849364aa67392c725](https://github.com/optimizely/optimizely/blob/910eca63e4f3eed8604542f849364aa67392c725/src/www/js/shared/cidr.js).
 *
 * TODO(tyler): see if there is a better one out there/publish this as a package if not.
 * @module
 */

/**
 * @param {string} cidr The CIDR specification string.
 * @constructor
 * @throws {Error} If cidr is an invalid specification.
 */
var CIDRClass = function(cidr:string) {
  /**
   * @type {string}
   */
  this.cidrString = cidr.trim();

  var parsedCIDR = parseCIDR(this.cidrString);
  if (parsedCIDR === null) {
    throw new Error('Invalid CIDR specification');
  }

  /**
   * @type {IP}
   */
  this.maskedAddress = parsedCIDR.maskedAddress;

  /**
   * @type {IP}
   */
  this.mask = parsedCIDR.mask;
};

/**
 * @typedef {number[]} IP
 * */

/**
 * @typedef {Object.<IP>} NetworkMask
 * */

/**
 * Number of bytes (octets) in an IP address.
 * @type {number}
 * @const
 */
var NUM_IP_OCTETS = 4;

/**
 * Reports whether this CIDR specification contains this ip in its range.
 * @param {string} ip The IP to test.
 * @return {boolean} Whether this CIDR contains ip in its range.
 * @throws {Error} If ip is invalid.
 */
CIDRClass.prototype.containsIP = function(ip:string): boolean {
  var parsedIp = parseIP(ip);
  if (parsedIp === null) {
    throw new Error('Invalid ip: ' + ip);
  }

  for (var i = 0; i < NUM_IP_OCTETS; i++) {
    if ((parsedIp[i] & this.mask[i]) !== this.maskedAddress[i]) {
      return false;
    }
  }
  return true;
};

/**
 * Parses the CIDR specification into its prefix ip and mask.
 * @param {string} cidr The CIDR specification string.
 * @return {optly.CIDR.NetworkMask} The network address and mask,
 *      or null if the specification is invalid.
 * @private
 */
var parseCIDR = function(cidr:string): import('plugins/feature_ip/helpers/cidr').NetworkMask {
  var parts = cidr.split('/');
  if (parts.length !== 2) {
    return null;
  }
  var numLeadingOnes = parseInt(parts[1], 10);
  if (isNaN(numLeadingOnes) || numLeadingOnes < 0 || numLeadingOnes > 32) {
    return null;
  }
  var ip = parseIP(parts[0]);
  if (ip === null) {
    return null;
  }
  var mask = makeMask(numLeadingOnes);
  for (var i = 0; i < NUM_IP_OCTETS; i++) {
    ip[i] = ip[i] & mask[i];
  }
  return {
    maskedAddress: ip,
    mask: mask
  };
};

/**
 * Parse ip string into an array of integers, one per octet.
 * @param {string} ip The ip string in n.n.n.n format.
 * @return {optly.CIDR.IP} The ip converted to an array of integers,
 *      one per octet, or null to indicate an invalid ip.
 * @private
 */
var parseIP = function(ip:string): import('plugins/feature_ip/helpers/cidr').IP {
  var parts = ip.split('.');
  if (parts.length !== NUM_IP_OCTETS) {
    return null;
  }

  var parsedIp = [];
  for (var i = 0; i < NUM_IP_OCTETS; i++) {
    var octet = parseOctet(parts[i]);
    if (octet === null) {
      return null;
    }
    parsedIp[i] = octet;
  }
  return parsedIp;
};

/**
 * Parse ip octet.
 * @param {string} octet The ip octet to parse.
 * @return {?number} The octet value in [0, 255],
 *      or null to indicate an invalid octet.
 * @private
 */
var parseOctet = function(octet:string): number {
  if (octet.length > 3) {
    return null;
  }
  var value = parseInt(octet, 10);
  if (isNaN(value)) {
    return null;
  }
  // Does octet have trailing non-numerals?
  if (octet !== value.toString()) {
    return null;
  }
  if (value < 0 || value > 255) {
    return null;
  }
  return value;
};

/**
 * Lookup table of leading ones in a byte. The index is the number
 * of leading ones required.
 * For example, CIDR.ONES_LOOKUP_TABLE[2] = 11000000 (binary) = 0xc0.
 * @type {!Array.<number>}
 * @const
 * @private
 */
var ONES_LOOKUP_TABLE = [
  0x00, 0x80, 0xc0, 0xe0, 0xf0, 0xf8, 0xfc, 0xfe, 0xff
];

/**
 * Returns a 32-bit mask with the specified number of leading ones.
 * @param {number} numLeadingOnes Number of leading ones. Must be in [0, 32].
 * @return {CIDR.IP} The mask with the specified leading ones,
 *      or null if numLeadingOnes is out of bounds.
 * @private
 */
var makeMask = function(numLeadingOnes:number): import('plugins/feature_ip/helpers/cidr').IP {
  if (numLeadingOnes < 0 || numLeadingOnes > 32) {
    return null;
  }
  var mask = [];
  for (var i = 0; i < NUM_IP_OCTETS; i++) {
    mask[i] = 0;
  }
  var numFullOctets = Math.floor(numLeadingOnes / 8);
  var numRemainingBits = numLeadingOnes % 8;
  for (i = 0; i < numFullOctets; i++) {
    mask[i] = 0xff;
  }
  if (numFullOctets < NUM_IP_OCTETS) {
    mask[numFullOctets] = ONES_LOOKUP_TABLE[numRemainingBits];
  }
  return mask;
};

module.exports = {
  CIDR: CIDRClass
};

if (__TEST__) {
  module.exports = {
    CIDR: CIDRClass,
    parseCIDR: parseCIDR,
    parseIP: parseIP,
    parseOctet: parseOctet,
    makeMask: makeMask,
    ONES_LOOKUP_TABLE: ONES_LOOKUP_TABLE,
  };
}


